'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import type { AddToCartUx } from '@/api/variocube';
import {
  ProductModalVariantProduct,
  shouldShowSizeSelector
} from '@/feature/product-modal-variant';
import { UiProductCounterInput } from '@/ui/product-counter-input';
import { UiKitButton } from '@/ui-kit/button';

import { AddToCartRecommendations } from '../AddToCartRecommendations';
import { ProductTile } from '../ProductTile/ProductTile';
import { ProductVariantSelector } from '../ProductVariantSelector/ProductVariantSelector';

import { styles } from './ProductVariants.styles';

export interface ProductVariantsProps {
  selectedVariantId: string | null;
  onVariantClick: (variantId: string) => void;
  onAddToCart: () => void;
  onContinueShopping?: () => void;
  product: ProductModalVariantProduct;
  isLoading?: boolean;
  isInCart?: boolean;
  cartQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  maxOfferQuantity?: number;
  onClose: () => void;
  ux?: AddToCartUx;
  /** Товар добавлен в рамках текущего открытия шторки (B/C) */
  isAddedInSession?: boolean;
}

export function ProductVariants({
  product,
  selectedVariantId,
  onVariantClick,
  onAddToCart,
  onContinueShopping,
  isLoading = false,
  isInCart = false,
  cartQuantity = 1,
  onQuantityChange,
  maxOfferQuantity,
  onClose,
  ux = 'legacy',
  isAddedInSession = false
}: ProductVariantsProps) {
  const t = useTranslations();
  const isExperiment = ux === 'afterAdd' || ux === 'withBlock';
  const slots = styles({ experiment: isExperiment });

  const needsSize = shouldShowSizeSelector(product.productOffers);
  const isAdded = isExperiment ? isAddedInSession || isInCart : isInCart;
  const showSizeSelector = needsSize && !isAdded;
  const showQuantity = isExperiment && !isAdded;
  const quantityDisabled = needsSize && !selectedVariantId;
  const showRecommendations =
    ux === 'withBlock' || (ux === 'afterAdd' && isAdded);
  const actionsDisabled = needsSize && !selectedVariantId;

  const router = useRouter();

  const handleToCart = () => {
    router.push('/cart');
    onClose();
  };

  const title = isExperiment
    ? isAdded
      ? t('productVariantModal.titleAdded')
      : t('productVariantModal.titleAdd')
    : t('productVariantModal.title');

  const renderQuantity = (className?: string) => (
    <div className={slots.quantityCollapse({ visible: showQuantity, className })}>
      <div className={slots.quantityCollapseInner()}>
        <div className={slots.quantitySection()}>
          <h3 className={slots.quantityTitle()}>{t('productVariantModal.count')}</h3>
          <UiProductCounterInput
            className={slots.quantityInput()}
            value={cartQuantity}
            onChange={onQuantityChange}
            max={maxOfferQuantity}
            min={1}
            hideDeleteIcon
            disabled={quantityDisabled}
            loading={isLoading && cartQuantity > 0}
          />
        </div>
      </div>
    </div>
  );

  const renderActions = (className?: string) => (
    <div className={slots.footer({ className })}>
      <UiKitButton
        className={slots.addToCart()}
        loading={isLoading && !isAdded}
        disabled={actionsDisabled || isLoading}
        onClick={isAdded ? handleToCart : onAddToCart}
      >
        {isAdded
          ? t('productVariantModal.checkout')
          : t('productVariantModal.addToCart')}
      </UiKitButton>
      <UiKitButton
        className={slots.continueShopping()}
        color="tertiary"
        disabled={actionsDisabled || isLoading}
        onClick={onContinueShopping ?? onClose}
      >
        {t('productVariantModal.continueShopping')}
      </UiKitButton>
    </div>
  );

  if (!isExperiment) {
    return (
      <div className={slots.base()}>
        <h2 className={slots.title()}>{title}</h2>
        {needsSize && (
          <ProductVariantSelector
            linkProduct={product?.link}
            variants={product.productOffers}
            selectedVariantId={selectedVariantId}
            onVariantClick={onVariantClick}
          />
        )}
        <div className={slots.actions()}>
          {isInCart && (
            <UiProductCounterInput
              value={cartQuantity}
              onChange={onQuantityChange}
              max={maxOfferQuantity}
              loading={isLoading && cartQuantity > 0}
            />
          )}
          {isInCart ? (
            <UiKitButton
              className={slots.addToCart()}
              disabled={isLoading}
              onClick={handleToCart}
            >
              <span className={slots.toCart()}>{t('goToCart')}</span>
              <span className={slots.toCartMobile()}>{t('addToCart')}</span>
            </UiKitButton>
          ) : (
            <UiKitButton
              className={slots.addToCart()}
              loading={isLoading}
              disabled={!selectedVariantId}
              onClick={onAddToCart}
            >
              {t('productVariantModal.addToCart')}
            </UiKitButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={slots.base()}>
      <div className={slots.content()}>
        <h2 className={slots.title()}>{title}</h2>

        <div className={slots.productTile()}>
          <ProductTile
            product={product}
            selectedVariantId={selectedVariantId}
            quantitySlot={renderQuantity(slots.quantityDesktop())}
          />
        </div>

        {renderQuantity(slots.quantityMobile())}

        <div className={slots.sizeCollapse({ visible: showSizeSelector })}>
          <div className={slots.sizeCollapseInner()}>
            {needsSize && (
              <div className={slots.variants()}>
                <ProductVariantSelector
                  linkProduct={product?.link}
                  variants={product.productOffers}
                  selectedVariantId={selectedVariantId}
                  onVariantClick={onVariantClick}
                  showTitle
                  accentHelper
                />
              </div>
            )}
          </div>
        </div>

        {renderActions(slots.footerDesktop())}

        <AddToCartRecommendations
          title={t('productVariantModal.recommendationsTitle')}
          visible={showRecommendations}
          sku={product.sku || product.slug}
          excludeProductUuid={product.uuid}
        />
      </div>

      {renderActions(slots.footerMobile())}
    </div>
  );
}
